import { isClient } from '../../Auth/utils/env';

const localDomainPortMap: Record<string, number> = {
  checkout: 3000,
  'sales-history': 3001,
};

const getBaseUrlParts = (fanaticsCollectBaseUrl: string) => {
  const baseUrlParts = fanaticsCollectBaseUrl.split('://');

  return baseUrlParts.length !== 2
    ? { protocol: 'https', baseUrl: baseUrlParts[0]?.replace('www.', '') }
    : {
        protocol: baseUrlParts[0],
        baseUrl: baseUrlParts[1]?.replace('www.', ''),
      };
};

export const generateMonolithUrl = (
  fanaticsCollectBaseUrl: string,
  path?: string,
  domain = 'www'
) => {
  const { protocol, baseUrl } = getBaseUrlParts(fanaticsCollectBaseUrl);
  const subDomain =
    (baseUrl.includes('pwccmp') || baseUrl.includes('fanaticsmp')) &&
    domain === 'www'
      ? ''
      : `${domain}.`;

  // if developing locally
  if (
    fanaticsCollectBaseUrl.includes('.test') ||
    fanaticsCollectBaseUrl.includes('localhost')
  ) {
    if (domain in localDomainPortMap) {
      return `http://localhost:${localDomainPortMap[domain]}${path}`;
    }
  }

  return `${protocol}://${subDomain}${baseUrl}${path}`;
};

export const getMonolithUrls = (baseUrl = 'fanaticscollect.com') => ({
  fanaticsLiveUrl: 'https://www.fanatics.live/home',
  fanaticsCollectUrl: 'https://fanaticscollect.com',
  cartUrl: generateMonolithUrl(baseUrl, '/cart'),
  buyUrl: generateMonolithUrl(baseUrl, '/how-to-buy'),
  sellUrl: generateMonolithUrl(baseUrl, '/how-to-sell'),
  gradeUrl: generateMonolithUrl(baseUrl, '/how-to-grade'),
  homeUrl: generateMonolithUrl(baseUrl, '/'),
  adminUrl: generateMonolithUrl(baseUrl, '/', 'admin'),
  loginUrl: generateMonolithUrl(baseUrl, '/login'),
  signupUrl: generateMonolithUrl(baseUrl, '/join'),
  collectLoginUrl: generateMonolithUrl(baseUrl, '/auth/login'),
  collectRegisterUrl: generateMonolithUrl(baseUrl, '/auth/register'),
  forgotPasswordUrl: generateMonolithUrl(baseUrl, '/password/reset'),
  weeklyAuctionUrl: generateMonolithUrl(baseUrl, '/weekly-auction'),
  weeklyAuctionItemsUrl: generateMonolithUrl(baseUrl, '/items'),
  premierAuctionUrl: generateMonolithUrl(baseUrl, '/premier-auction'),
  fixedPriceUrl: generateMonolithUrl(baseUrl, '/vault-marketplace'),
  premierAuctionApplicationUrl: generateMonolithUrl(
    baseUrl,
    '/premier-auction-application'
  ),
  updatePasswordUrl: generateMonolithUrl(baseUrl, '/password/change'),
  updatePhonedUrl: generateMonolithUrl(baseUrl, '/phone', 'members'),
  marketplaceAccountUrl: generateMonolithUrl(
    baseUrl,
    '/preferences#marketplace-card',
    'members'
  ),
  vaultAccountUrl: generateMonolithUrl(
    baseUrl,
    '/preferences#vault-card',
    'members'
  ),
  capitalAccountUrl: generateMonolithUrl(
    baseUrl,
    '/preferences#capital-card',
    'members'
  ),
  identityUrl: generateMonolithUrl(
    baseUrl,
    '/preferences#identity-card',
    'members'
  ),
  signedDocumentsUrl: generateMonolithUrl(baseUrl, '/documents', 'members'),
  termsAndConditionsUrl: generateMonolithUrl(baseUrl, '/documents', 'members'),
  emailSubscriptionUrl: generateMonolithUrl(
    baseUrl,
    '/preferences#email-card',
    'members'
  ),
  privacyPolicyUrl:
    'https://help.fanaticscollect.com/hc/en-us/articles/18433288548509-Privacy',
  termsOfServiceUrl: generateMonolithUrl(baseUrl, '/tos'),
  termsOfUseUrl:
    'https://help.fanaticscollect.com/hc/en-us/articles/18433319094045-Terms-of-Use',
  helpCenterUrl: 'https://help.fanaticscollect.com/hc/en-us',
  fanaticsUrl: 'https://www.fanatics.com/',
  fanIdSupportUrl:
    'https://help.fanaticscollect.com/hc/en-us/articles/19622634971549-FanID-Support',
  cookiePolicyUrl:
    'https://help.fanaticscollect.com/hc/en-us/articles/18433744789789-Cookie-Policy',
  returnPolicyUrl: generateMonolithUrl(baseUrl, '/returns'),
  employeePolicyUrl:
    'https://help.fanaticscollect.com/hc/en-us/articles/18447239356957-Employee-policy',
  payInvoiceUrl: generateMonolithUrl(baseUrl, '/invoices', 'checkout'),
  submissionUrl: generateMonolithUrl(baseUrl, '/submissions/create', 'members'),
  faqUrl:
    'https://help.fanaticscollect.com/hc/en-us/articles/18941423574685-Frequently-Asked-Questions',
  dealerProgram: 'https://www.fanaticscollect.com/dealer-program',
  affiliates: 'https://info.fanaticscollect.com/affiliate-referral-page-buyer',
  whatsNew: 'https://www.fanaticscollect.com/releases',
  adminUsersUrl: generateMonolithUrl(baseUrl, '/users', 'admin'),
  aboutTheVaultUrl:
    'https://help.fanaticscollect.com/hc/en-us/articles/19008715653661-The-Vault',
  salesHistoryUrl: generateMonolithUrl(baseUrl, '', 'sales-history'),
  marketIndicesUrl: generateMonolithUrl(baseUrl, '/market-indices'),
  definitiveGuidesUrl: generateMonolithUrl(baseUrl, '/definitive-guides'),
  newsroomUrl: generateMonolithUrl(baseUrl, '/newsroom'),
  eyeAppealUrl: generateMonolithUrl(baseUrl, '/eye-appeal'),
  marketplaceTenetsUrl:
    'https://help.fanaticscollect.com/hc/en-us/articles/18866648239133-Marketplace-Tenets',
  careersUrl: 'https://www.fanaticsinc.com/careers',
  contactUsUrl: generateMonolithUrl(baseUrl, '/contact-us'),
  memberSummaryUrl: generateMonolithUrl(baseUrl, '/', 'members'),
  memberVaultUrl: generateMonolithUrl(baseUrl, '/vault', 'members'),
  memberMySellingUrl: generateMonolithUrl(
    baseUrl,
    '/marketplace/my-selling/auctions',
    'members'
  ),
  memberMyBuyingUrl: generateMonolithUrl(
    baseUrl,
    '/marketplace/my-buying/bid-history/weekly-sunday-auction',
    'members'
  ),
  memberMyCapitalUrl: generateMonolithUrl(
    baseUrl,
    '/capital/advances',
    'members'
  ),
  memberMySubmissionsUrl: generateMonolithUrl(
    baseUrl,
    '/submissions',
    'members'
  ),
  memberMyAccountingUrl: generateMonolithUrl(baseUrl, '/invoices', 'members'),
  memberMyPreferencesUrl: generateMonolithUrl(
    baseUrl,
    '/preferences',
    'members'
  ),
  auctionScheduleUrl: generateMonolithUrl(
    baseUrl,
    '/api/marketplaces/calendar'
  ),
  siteWideMessageUrl: generateMonolithUrl(
    baseUrl,
    '/api/nav/site-wide-message'
  ),
  aboutTheMarketplaceUrl:
    'https://help.fanaticscollect.com/hc/en-us/articles/18433146043933-How-It-Works',
  aboutAuthentication: generateMonolithUrl(baseUrl, '/authentication'),
  marketplaceTermsAndConditionsUrl: generateMonolithUrl(
    baseUrl,
    '/marketplace-tos'
  ),
  vaultServicesAgreementUrl: generateMonolithUrl(
    baseUrl,
    '/vault-services-agreement'
  ),
  searchesAppUrl: generateMonolithUrl(baseUrl, '/apps/searches'),
  phoneNumberHelpUrl:
    'https://help.fanaticscollect.com/hc/en-us/articles/20280541507869-Login-Issues-Q-A',
  myBuyingUrl: generateMonolithUrl(
    baseUrl,
    '/marketplace/my-buying/bid-history/fixed-price',
    'members'
  ),
  mySellingUrl: generateMonolithUrl(baseUrl, '/marketplace/offers', 'members'),
  payoutsCreateUrl: generateMonolithUrl(baseUrl, '/payouts/create', 'members'),
  payoutsUrl: generateMonolithUrl(baseUrl, '/payouts', 'members'),
  privateSalesUrl: 'https://www.fanaticscollect.com/private-sales',
  fanaticsMarkets: 'https://fanaticsmarkets.com/',
  fanaticsSportsbookUrl: 'https://betfanatics.com/',
  toppsUrl: 'https://www.topps.com/',
  sellYourCards: 'https://www.fanaticscollect.com/sell',
});

export interface IMonolithUrls extends ReturnType<typeof getMonolithUrls> {}
export type IMonolithUrlKeys = keyof IMonolithUrls;

export const SUB_DOMAINS = {
  Admin: 'admin',
  Members: 'members',
  Checkout: 'checkout',
  SalesHistory: 'sales-history',
} as const;

export const ROUTES = {
  home: () => ['/'],
  homeLogin: () => ['/?l'],
  marketplace: () => ['/marketplace'],
  weeklySRP: () => [`/marketplace?type=WEEKLY`],
  premierSRP: () => [`/marketplace?type=PREMIER`],
  fixedSRP: () => [`/marketplace?type=FIXED`],
  salesHistory: () => [`/sales-history?type=WEEKLY&status=SOLD`],
  invoices: () => [`/invoices`, SUB_DOMAINS.Members],
  submissions: () => [`/submissions`, SUB_DOMAINS.Members],
  submissionCreate: () => ['/submissions/create', SUB_DOMAINS.Members],
  preferences: () => [`/preferences`, SUB_DOMAINS.Members],
  vault: () => [`/vault`, SUB_DOMAINS.Members],
  mySelling: () => [`/marketplace/my-selling/auctions`, SUB_DOMAINS.Members],
  myBuying: () => [
    `/marketplace/my-buying/bid-history/weekly-sunday-auction`,
    SUB_DOMAINS.Members,
  ],
  memberSummary: () => [`/`, SUB_DOMAINS.Members],

  // Collection
  collection: () => ['/collection'],

  // Activity routes
  activityOrders: () => ['/activity/orders'],
  activityBids: () => ['/activity/bids'],
  activityOffers: () => ['/activity/offers/buying'],
  activitySelling: () => ['/activity/selling/active/buy-now'],
  activityAccounting: () => ['/activity/accounting/marketplace'],
  activityBidsActiveWeekly: () => ['/activity/bids/active/auction'],
  activityWatchlist: () => ['/activity/watchlist'],
  activityWatchlistBuyNow: () => ['/activity/watchlist/active/buy-now'],

  // Legacy routes
  legacyWeeklySRP: () => ['/weekly-auction'],
  legacyPremierSRP: () => ['/premier-auction'],
  legacyFixedSRP: () => ['/vault-marketplace'],
  adminHome: () => ['/', SUB_DOMAINS.Admin],
  payoutsCreate: () => ['/payouts/create', SUB_DOMAINS.Members],
  payouts: () => ['/payouts', SUB_DOMAINS.Members],
} as const satisfies {
  [key: string]: (...args: string[]) => [pathname: string, subDomain?: string];
};

export type RoutesKeys = keyof typeof ROUTES;

export const generateUrl = (
  collectBaseUrl: string,
  path: string,
  domain?: string,
  external?: boolean
) => {
  const { protocol, baseUrl: originalBaseUrl } =
    getBaseUrlParts(collectBaseUrl);

  // Check current host for 'local.dev'
  const currentHost =
    typeof window !== 'undefined' ? window.location.hostname : '';
  const isLocalDev = currentHost.includes('local.dev');

  // Replace 'dev.' with 'local.dev.' if needed
  const baseUrl =
    !external && !domain && isLocalDev
      ? originalBaseUrl.replace(/^dev\./, 'local.dev.')
      : originalBaseUrl;

  const subDomain = domain ? `${domain}.` : '';

  return `${protocol}://${subDomain}${baseUrl}${path}`;
};

export const createGetLinkFn = (
  fanaticsCollectBaseUrl: string,
  getCurrentUrl: () => string
) => {
  return function getLink<TRouteKey extends RoutesKeys>(
    routeKey: TRouteKey,
    queryParams?: Record<string, string>,
    ...args: Parameters<(typeof ROUTES)[TRouteKey]>
  ): string {
    const currentUrl = new URL(getCurrentUrl());

    const getUrlOptions = (): [
      baseUrl: string,
      pathname: string,
      subDomain?: string
    ] => {
      const pathFunction = ROUTES[routeKey];
      // @ts-expect-error TODO: fix this
      const [pathname, subDomain] = pathFunction(...args);

      const containsHosts = (hosts: string[]) => {
        return hosts.some((host) => currentUrl.hostname.includes(host));
      };

      if (containsHosts(['localhost', 'local.dev'])) {
        return ['https://local.dev.fanaticsmp.com', pathname];
      }

      if (containsHosts(['pwccmp', 'fanaticsmp'])) {
        return [fanaticsCollectBaseUrl, pathname, subDomain];
      }

      return [fanaticsCollectBaseUrl, pathname, subDomain ?? 'www'];
    };

    const url = new URL(generateUrl(...getUrlOptions()));
    url.search = new URLSearchParams({
      ...Object.fromEntries(url.searchParams),
      ...queryParams,
    }).toString();

    return url.toString();
  };
};

export const getLink = (
  fanaticsCollectBaseUrl: string,
  routeKey: RoutesKeys,
  queryParams?: Record<string, string>
) => {
  return createGetLinkFn(fanaticsCollectBaseUrl, () =>
    isClient() ? window.location.href : fanaticsCollectBaseUrl
  )(routeKey, queryParams);
};
